import * as React from 'react';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import CheapestTariffs from './CheapestTariffs';
import ElectricityRatesChart from './ElectricityRatesChart';
import CarbonIntensityChart from './CarbonIntensityChart';
import ApiClient from '../../utils/apiClient';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import {
  getCurrentPeriodKey,
  getPeriodCachedData,
  setPeriodCachedData,
  cleanupPeriodCache,
  getEnergyInsightsPreferences,
  setEnergyInsightsPreferences
} from '../../utils/cacheUtils';

interface RateDataItem {
  timestamp: string;
  rate: number;
  rate_delta?: number;
}

interface CarbonIntensityItem {
  timestamp: string;
  intensity: number;
  category: number;
  intensity_delta?: number;
}

export default function EnergyInsights() {
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [ratesData, setRatesData] = React.useState<RateDataItem[]>([]);
  const [carbonIntensityData, setCarbonIntensityData] = React.useState<CarbonIntensityItem[]>([]);
  const [tariffInfo, setTariffInfo] = React.useState<string>('');
  const [carbonIntensityRegionName, setCarbonIntensityRegionName] = React.useState<string>('');
  const [chartsLoading, setChartsLoading] = React.useState<boolean>(true);
  const [chartsError, setChartsError] = React.useState<string | null>(null);
  
  // Load cached preferences on mount
  const cachedPrefs = React.useMemo(() => getEnergyInsightsPreferences(), []);
  const [viewMode, setViewMode] = React.useState<'simplified' | 'detailed'>(
    cachedPrefs?.viewMode || 'simplified'
  );
  
  const [isAgile, setIsAgile] = React.useState<boolean>(false);
  const isInitialLoadRef = React.useRef<boolean>(true);

  const isFreeUser = () => {
    const access = localStorage.getItem("access");
    return !access || access === 'null' || access === 'free';
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Cache key generators
  const getElectricityRatesCacheKey = (): string => {
    return `electricity-rates-${getCurrentPeriodKey()}`;
  };

  const getCarbonIntensityCacheKey = (): string => {
    return `carbon-intensity-${getCurrentPeriodKey()}`;
  };

  // Create a stable reference for the fetchChartData function
  const fetchChartData = React.useCallback(async () => {
    try {
      // Only show loading spinner on initial load
      if (isInitialLoadRef.current) {
        setChartsLoading(true);
      }
      setChartsError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Check cache for electricity rates
      const electricityRatesCacheKey = getElectricityRatesCacheKey();
      const cachedRatesData = getPeriodCachedData<{
        data: RateDataItem[];
        tariff_info: string;
        is_agile: boolean;
      }>(electricityRatesCacheKey);

      if (cachedRatesData) {
        // Use cached data
        setRatesData(cachedRatesData.data);
        setTariffInfo(cachedRatesData.tariff_info || '');
        setIsAgile(cachedRatesData.is_agile || false);
      } else {
        // Cache miss - fetch from API
        const ratesData = await ApiClient.get('api/products/electricity-rates');
        if (ratesData.success && ratesData.data) {
          // Store in cache
          setPeriodCachedData(electricityRatesCacheKey, {
            data: ratesData.data,
            tariff_info: ratesData.tariff_info || '',
            is_agile: ratesData.is_agile || false
          });
          
          // Clean up old cache entries
          cleanupPeriodCache(['electricity-rates-']);
          
          setRatesData(ratesData.data);
          setTariffInfo(ratesData.tariff_info || '');
          setIsAgile(ratesData.is_agile || false);
        } else {
          throw new Error('Invalid electricity rates response format');
        }
      }

      // Check cache for carbon intensity
      const carbonIntensityCacheKey = getCarbonIntensityCacheKey();
      const cachedCarbonData = getPeriodCachedData<{
        data: CarbonIntensityItem[];
        region_name: string;
      }>(carbonIntensityCacheKey);

      if (cachedCarbonData) {
        // Use cached data
        setCarbonIntensityData(cachedCarbonData.data);
        setCarbonIntensityRegionName(cachedCarbonData.region_name || '');
      } else {
        // Cache miss - fetch from API
        const carbonData = await ApiClient.get('api/carbon/upcoming-intensity');
        if (carbonData.success && carbonData.data) {
          // Store in cache
          setPeriodCachedData(carbonIntensityCacheKey, {
            data: carbonData.data,
            region_name: carbonData.region_name || ''
          });
          
          // Clean up old cache entries
          cleanupPeriodCache(['carbon-intensity-']);
          
          setCarbonIntensityData(carbonData.data);
          setCarbonIntensityRegionName(carbonData.region_name || '');
        } else {
          throw new Error('Invalid carbon intensity response format');
        }
      }

    } catch (error) {
      console.error('Error fetching chart data:', error);
      setChartsError(error instanceof Error ? error.message : 'Failed to fetch chart data');
    } finally {
      setChartsLoading(false);
      isInitialLoadRef.current = false;
    }
  }, []);

  // Initial load and polling
  React.useEffect(() => {
    fetchChartData();

    // Set up polling to refresh data every 5 minutes
    const interval = setInterval(() => {
      fetchChartData();
    }, 5 * 60 * 1000); // 5 minutes in milliseconds

    return () => {
      clearInterval(interval);
    };
  }, [fetchChartData]);

  // Save preferences to cache whenever viewMode changes
  React.useEffect(() => {
    setEnergyInsightsPreferences({ viewMode });
  }, [viewMode]);

  // Listen for dashboard refresh events
  React.useEffect(() => {
    const handleDashboardRefresh = () => {
      setRefreshKey(prev => prev + 1);
    };

    window.addEventListener('dashboardRefresh', handleDashboardRefresh);
    return () => {
      window.removeEventListener('dashboardRefresh', handleDashboardRefresh);
    };
  }, []);

  return (
    <Box>
      <Box sx={{ px: { xs: 1, md: 0 } }}>
        <Typography variant="h1" sx={{ mb: 3, mt: 2 }}>
          Your Energy Tariff
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt: 2 }}>
          <Typography variant="h2">
            Upcoming {(isMobile) ? 'rates' : 'prices and emissions' }
          </Typography>
          {!isFreeUser() && (
            <Box sx={{my: -1, py: -1}}>
              {(!isMobile) && (
                <Typography variant="h6" gutterBottom>
                  View
                </Typography>)}
              <FormControl size="small" sx={{ minWidth: 140, mb: -1, pb: -1 }}>                
                <Select
                  labelId="view-mode-label"
                  id="view-mode-select"
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value as 'simplified' | 'detailed')}
                >
                  <MenuItem value="simplified">Simple</MenuItem>
                  <MenuItem value="detailed">Detailed</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
      </Box>

      {/* Electricity Rates and Carbon Intensity Charts */}
      {chartsLoading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px',
          flexDirection: 'column',
          gap: 2,
          mb: 3
        }}>
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            Loading tariff data...
          </Typography>
        </Box>
      ) : chartsError ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px',
          flexDirection: 'column',
          gap: 2,
          mb: 3
        }}>
          <Typography variant="h6" color="error">
            Error Loading Tariff Data
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: '400px' }}>
            {chartsError}
          </Typography>
        </Box>
      ) : ratesData.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px',
          flexDirection: 'column',
          gap: 2,
          mb: 3
        }}>
          <Typography variant="h6" color="text.secondary">
            No Electricity Tariff Rate Data Available
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: '400px' }}>
            No upcoming electricity rates are available for your current tariff. This may be because your tariff doesn't have variable rates or there's an issue with the data source.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2} columns={12} sx={{ width: '100%', mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ElectricityRatesChart 
              data={ratesData} 
              loading={chartsLoading}
              tariffInfo={tariffInfo}
              isAgile={isAgile}
              showDeltaChart={viewMode === 'detailed'}
              data-chart-id="electricity-rates-chart"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <CarbonIntensityChart 
              loading={chartsLoading}
              data={carbonIntensityData} 
              regionName={carbonIntensityRegionName}
              showDeltaChart={viewMode === 'detailed'}
              data-chart-id="carbon-intensity-chart"
            />
          </Grid>
        </Grid>
        
      )}

      {/* Analysis Section */}
      <Box sx={{ pt:2, px: { xs: 1, md: 0 } }}>
      <Typography variant="h2" sx={{ mb: 2 }}>
          Cheapest Tariff for You
        </Typography>
      </Box>
       
      {/* Cheapest Tariffs Analysis */}
      <CheapestTariffs
        key={`cheapest-tariffs-${refreshKey}`}
        days={30}
        consumptionTypeId={1} // Electricity
        paymentMethodId={1} // Direct Debit
      />              
    </Box>
  );
}
